import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, CheckCircle2, Clock, ShieldCheck, XCircle } from 'lucide-react';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { INDUSTRIES } from '@/lib/constants';
import {
  createProfile,
  getMyProfile,
  getMyVerificationRequest,
  isHandleTaken,
  updateProfile,
  uploadProfileMedia,
} from '@/lib/api/profiles';
import { useAuth } from '@/lib/hooks/useAuth';
import { cn } from '@/lib/utils/cn';

const OPEN_TO_OPTIONS = [
  { value: 'mentoring', label: 'Mentoring others' },
  { value: 'being_mentored', label: 'Being mentored' },
  { value: 'hiring', label: 'Hiring' },
  { value: 'being_hired', label: 'Open to work' },
  { value: 'investing', label: 'Investing' },
  { value: 'consulting', label: 'Consulting' },
] as const;

const handleRegex = /^[a-z0-9-]+$/;

const schema = z.object({
  display_name: z.string().min(1, 'Required').max(80),
  handle: z
    .string()
    .min(2, 'At least 2 characters')
    .max(40)
    .regex(handleRegex, 'Lowercase letters, numbers, and hyphens only'),
  headline: z.string().max(120).optional(),
  bio: z.string().max(2000).optional(),
  current_city: z.string().max(80).optional(),
  current_country: z.string().max(80).optional(),
  origin_city: z.string().max(80).optional(),
  origin_country: z.string().max(80).optional(),
  industry: z.string().optional(),
  role_title: z.string().max(100).optional(),
  company: z.string().max(100).optional(),
  linkedin_url: z.union([z.literal(''), z.string().url('Enter a valid URL')]),
  website_url: z.union([z.literal(''), z.string().url('Enter a valid URL')]),
  contact_email: z.union([z.literal(''), z.string().email('Enter a valid email')]),
  phone: z.string().max(30).optional(),
  telegram_handle: z.string().max(64).optional(),
  whatsapp: z.string().max(30).optional(),
  languages_raw: z.string().optional(),
  skills_raw: z.string().optional(),
  interests_raw: z.string().optional(),
  open_to: z.record(z.boolean()).optional(),
});
type FormValues = z.infer<typeof schema>;

function splitTags(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ProfileEdit() {
  const { user, loaded, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const successMessage = (location.state as { success?: string } | null)
    ?.success;

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['my-profile'],
    queryFn: getMyProfile,
    enabled: isAuthenticated,
  });

  const { data: verificationRequest } = useQuery({
    queryKey: ['my-verification-request'],
    queryFn: getMyVerificationRequest,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  const [handleError, setHandleError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const avatarFileRef = useRef<File | null>(null);
  const coverFileRef = useRef<File | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      display_name: '',
      handle: '',
      headline: '',
      bio: '',
      current_city: '',
      current_country: '',
      origin_city: '',
      origin_country: '',
      industry: '',
      role_title: '',
      company: '',
      linkedin_url: '',
      website_url: '',
      contact_email: '',
      phone: '',
      telegram_handle: '',
      whatsapp: '',
      languages_raw: '',
      skills_raw: '',
      interests_raw: '',
      open_to: {},
    },
  });

  // Populate form once profile loads.
  useEffect(() => {
    if (!profile) return;
    const openTo = profile.open_to as Record<string, boolean> | null;
    reset({
      display_name: profile.display_name ?? '',
      handle: profile.handle ?? '',
      headline: profile.headline ?? '',
      bio: profile.bio ?? '',
      current_city: profile.current_city ?? '',
      current_country: profile.current_country ?? '',
      origin_city: profile.origin_city ?? '',
      origin_country: profile.origin_country ?? '',
      industry: profile.industry ?? '',
      role_title: profile.role_title ?? '',
      company: profile.company ?? '',
      linkedin_url: profile.linkedin_url ?? '',
      website_url: profile.website_url ?? '',
      contact_email: profile.contact_email ?? '',
      phone: profile.phone ?? '',
      telegram_handle: profile.telegram_handle ?? '',
      whatsapp: profile.whatsapp ?? '',
      languages_raw: (profile.languages ?? []).join(', '),
      skills_raw: (profile.skills ?? []).join(', '),
      interests_raw: (profile.interests ?? []).join(', '),
      open_to: openTo ?? {},
    });
  }, [profile, reset]);

  // Redirect unauthenticated users.
  useEffect(() => {
    if (loaded && !isAuthenticated) navigate('/signin', { replace: true });
  }, [loaded, isAuthenticated, navigate]);

  const watchedHandle = watch('handle');
  useEffect(() => {
    if (!watchedHandle || !handleRegex.test(watchedHandle)) return;
    const id = window.setTimeout(async () => {
      try {
        const taken = await isHandleTaken(watchedHandle, profile?.id);
        setHandleError(taken ? 'This handle is already taken.' : null);
      } catch {
        // silently ignore transient check failures
      }
    }, 400);
    return () => window.clearTimeout(id);
  }, [watchedHandle, profile?.id]);

  const onSubmit = async (values: FormValues) => {
    if (handleError) return;
    setSubmitError(null);
    try {
      const fields = {
        display_name: values.display_name,
        handle: values.handle,
        headline: values.headline || null,
        bio: values.bio || null,
        current_city: values.current_city || null,
        current_country: values.current_country || null,
        origin_city: values.origin_city || null,
        origin_country: values.origin_country || null,
        industry: values.industry || null,
        role_title: values.role_title || null,
        company: values.company || null,
        linkedin_url: values.linkedin_url || null,
        website_url: values.website_url || null,
        contact_email: values.contact_email || null,
        phone: values.phone || null,
        telegram_handle: values.telegram_handle || null,
        whatsapp: values.whatsapp || null,
        languages: splitTags(values.languages_raw),
        skills: splitTags(values.skills_raw),
        interests: splitTags(values.interests_raw),
        open_to: values.open_to ?? {},
      };

      let saved = profile
        ? await updateProfile(profile.id, fields)
        : await createProfile(fields);

      // Upload media after profile exists (need the user id for the path).
      const uid = user!.id;
      if (avatarFileRef.current) {
        const url = await uploadProfileMedia(uid, 'avatar', avatarFileRef.current);
        saved = await updateProfile(saved.id, { ...fields, avatar_url: url } as typeof fields & { avatar_url: string });
      }
      if (coverFileRef.current) {
        const url = await uploadProfileMedia(uid, 'cover', coverFileRef.current);
        saved = await updateProfile(saved.id, { ...fields, cover_url: url } as typeof fields & { cover_url: string });
      }

      await queryClient.invalidateQueries({ queryKey: ['my-profile'] });

      // First-time profile creation: steer new users straight into verification.
      if (isNewProfile) {
        navigate('/me/verify', {
          replace: true,
          state: { fromSetup: true },
        });
      } else {
        navigate(`/profile/${saved.handle}`, {
          replace: true,
          state: { success: 'Profile saved.' },
        });
      }
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Save failed.';
      setSubmitError(msg);
    }
  };

  const handleMediaPick =
    (slot: 'avatar' | 'cover') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      if (slot === 'avatar') {
        avatarFileRef.current = file;
        setAvatarPreview(url);
      } else {
        coverFileRef.current = file;
        setCoverPreview(url);
      }
    };

  if (!loaded || profileLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-cream">
        <AppHeader />
        <main className="container flex-1 py-12">
          <Skeleton className="h-10 w-64" />
          <div className="mt-6 space-y-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full rounded-2xl" />
            ))}
          </div>
        </main>
      </div>
    );
  }

  const currentAvatar = avatarPreview ?? profile?.avatar_url;
  const currentCover = coverPreview ?? profile?.cover_url;
  const isNewProfile = !profile;

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <AppHeader />
      <main className="container max-w-2xl flex-1 py-12">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="font-display text-3xl text-navy">
            {isNewProfile ? 'Set up your profile' : 'Edit profile'}
          </h1>
          {!isNewProfile && profile.handle && (
            <Button asChild variant="ghost-dark" size="sm">
              <Link to={`/profile/${profile.handle}`}>View profile</Link>
            </Button>
          )}
        </div>

        {successMessage && (
          <div className="mb-6 flex items-center gap-2 rounded-xl bg-success/10 px-4 py-3 text-sm text-success">
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            {successMessage}
          </div>
        )}

        {!isNewProfile && <VerificationStatusCard profile={profile} request={verificationRequest ?? null} />}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* ── Cover + Avatar ─────────────────────────────────── */}
          <Card className="mb-6 overflow-hidden">
            <div
              className={cn(
                'relative h-32 w-full',
                currentCover
                  ? 'bg-center bg-cover'
                  : 'bg-gradient-to-br from-navy-darker via-navy-deep to-navy',
              )}
              style={
                currentCover ? { backgroundImage: `url(${currentCover})` } : {}
              }
            >
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center bg-navy/30 opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
                aria-label="Change cover photo"
              >
                <Camera className="h-6 w-6 text-cream" aria-hidden="true" />
              </button>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                aria-label="Upload cover photo"
                onChange={handleMediaPick('cover')}
              />
            </div>
            <div className="relative px-6 pb-6">
              <div className="relative -mt-10 inline-block">
                <Avatar
                  name={watch('display_name') || 'You'}
                  src={currentAvatar}
                  size="xl"
                  className="border-4 border-white shadow-md"
                />
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-navy/40 opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
                  aria-label="Change avatar"
                >
                  <Camera className="h-5 w-5 text-cream" aria-hidden="true" />
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  aria-label="Upload avatar"
                  onChange={handleMediaPick('avatar')}
                />
              </div>
              <p className="mt-3 text-xs text-muted">
                Click avatar or cover to upload. Max 5 MB, JPEG/PNG/WebP.
              </p>
            </div>
          </Card>

          {/* ── Basic info ─────────────────────────────────────── */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Basic info</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Field
                label="Name"
                id="display_name"
                required
                error={errors.display_name?.message}
              >
                <Input id="display_name" {...register('display_name')} aria-invalid={!!errors.display_name} />
              </Field>
              <Field
                label="Handle"
                id="handle"
                hint="cpn.com/profile/your-handle"
                required
                error={errors.handle?.message ?? handleError ?? undefined}
              >
                <div className="flex items-center gap-1">
                  <span className="shrink-0 text-sm text-muted">@</span>
                  <Input
                    id="handle"
                    {...register('handle')}
                    aria-invalid={!!(errors.handle || handleError)}
                    className="lowercase"
                    onInput={(e) => {
                      const el = e.currentTarget;
                      el.value = el.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    }}
                  />
                </div>
              </Field>
              <Field label="Headline" id="headline" error={errors.headline?.message}>
                <Input
                  id="headline"
                  placeholder="e.g. Software engineer & mentor"
                  {...register('headline')}
                />
              </Field>
              <Field label="Bio" id="bio" error={errors.bio?.message}>
                <Textarea
                  id="bio"
                  rows={5}
                  placeholder="Tell the community a bit about yourself."
                  {...register('bio')}
                />
              </Field>
            </CardContent>
          </Card>

          {/* ── Professional ──────────────────────────────────── */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Professional</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Field label="Industry" id="industry" error={errors.industry?.message}>
                <select
                  id="industry"
                  {...register('industry')}
                  className={cn(
                    'flex h-11 w-full rounded-lg border border-border bg-white px-4 py-2 text-sm text-ink shadow-sm',
                    'focus-visible:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30',
                  )}
                >
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Role / Title" id="role_title" error={errors.role_title?.message}>
                  <Input id="role_title" {...register('role_title')} />
                </Field>
                <Field label="Company" id="company" error={errors.company?.message}>
                  <Input id="company" {...register('company')} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="LinkedIn URL" id="linkedin_url" error={errors.linkedin_url?.message}>
                  <Input id="linkedin_url" type="url" placeholder="https://linkedin.com/in/…" {...register('linkedin_url')} />
                </Field>
                <Field label="Website" id="website_url" error={errors.website_url?.message}>
                  <Input id="website_url" type="url" placeholder="https://…" {...register('website_url')} />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* ── Contact & social ──────────────────────────────── */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Contact & social</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <p className="text-xs text-muted">
                All fields are optional and public — only share what you're comfortable with.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Public email" id="contact_email" error={errors.contact_email?.message}>
                  <Input id="contact_email" type="email" placeholder="you@example.com" {...register('contact_email')} />
                </Field>
                <Field label="Phone number" id="phone" error={errors.phone?.message}>
                  <Input id="phone" type="tel" placeholder="+1 555 000 0000" {...register('phone')} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Telegram username" id="telegram_handle" hint="Without the @" error={errors.telegram_handle?.message}>
                  <div className="flex items-center gap-1">
                    <span className="shrink-0 text-sm text-muted">@</span>
                    <Input id="telegram_handle" placeholder="username" {...register('telegram_handle')} />
                  </div>
                </Field>
                <Field label="WhatsApp number" id="whatsapp" error={errors.whatsapp?.message}>
                  <Input id="whatsapp" type="tel" placeholder="+1 555 000 0000" {...register('whatsapp')} />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* ── Location ──────────────────────────────────────── */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Location</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Current city" id="current_city">
                  <Input id="current_city" {...register('current_city')} />
                </Field>
                <Field label="Current country" id="current_country">
                  <Input id="current_country" {...register('current_country')} />
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Origin city" id="origin_city">
                  <Input id="origin_city" {...register('origin_city')} />
                </Field>
                <Field label="Origin country" id="origin_country">
                  <Input id="origin_country" {...register('origin_country')} />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* ── Tags ──────────────────────────────────────────── */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Skills & interests</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {(
                [
                  { id: 'languages_raw', label: 'Languages', placeholder: 'Khmer, English, French' },
                  { id: 'skills_raw', label: 'Skills', placeholder: 'Product management, Python, Finance' },
                  { id: 'interests_raw', label: 'Interests', placeholder: 'Startups, Muay Thai, Cooking' },
                ] as const
              ).map((f) => (
                <Field key={f.id} label={f.label} id={f.id} hint="Comma-separated">
                  <Input id={f.id} placeholder={f.placeholder} {...register(f.id)} />
                </Field>
              ))}
            </CardContent>
          </Card>

          {/* ── Open to ───────────────────────────────────────── */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Open to</CardTitle>
            </CardHeader>
            <CardContent>
              <fieldset>
                <legend className="sr-only">Open to opportunities</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {OPEN_TO_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border accent-gold focus-visible:ring-2 focus-visible:ring-gold/40"
                        {...register(`open_to.${opt.value}`)}
                      />
                      <span className="text-sm text-navy">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </CardContent>
          </Card>

          {submitError && (
            <p
              role="alert"
              aria-live="polite"
              className="mb-4 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              {submitError}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              type="submit"
              variant="gold"
              size="lg"
              disabled={isSubmitting || !!handleError || (!isDirty && !avatarFileRef.current && !coverFileRef.current)}
            >
              {isSubmitting ? 'Saving…' : isNewProfile ? 'Create profile' : 'Save changes'}
            </Button>
            {!isNewProfile && profile?.handle && (
              <Button asChild variant="ghost-dark" size="lg">
                <Link to={`/profile/${profile.handle}`}>Cancel</Link>
              </Button>
            )}
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}

type ProfileRow = import('@/lib/supabase/types').Database['public']['Tables']['profiles']['Row'];
type VerificationRequest = import('@/lib/api/profiles').VerificationRequest;

function VerificationStatusCard({
  profile,
  request,
}: {
  profile: ProfileRow | null | undefined;
  request: VerificationRequest | null;
}) {
  if (!profile) return null;

  const isVerified =
    (profile.verification as string) === 'verified' ||
    (profile.verification as string) === 'admin_verified';

  if (isVerified) return null;

  if (request?.status === 'pending') {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-navy">Verification pending</p>
          <p className="mt-0.5 text-xs text-ink/70">
            Submitted {new Date(request.created_at).toLocaleDateString()}. We'll review and notify you shortly.
          </p>
        </div>
      </div>
    );
  }

  if (request?.status === 'rejected') {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-navy">Verification not approved</p>
          {request.review_reason && (
            <p className="mt-0.5 text-xs text-ink/70">{request.review_reason}</p>
          )}
          <Link
            to="/me/verify"
            className="mt-1 inline-block text-xs font-medium text-gold-dark underline underline-offset-2"
          >
            Submit a new request
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-start gap-3 rounded-xl border border-border/70 bg-white px-4 py-3">
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-navy">Get verified</p>
        <p className="mt-0.5 text-xs text-ink/70">
          Submit your LinkedIn profile for admin review to earn a Verified badge.
        </p>
      </div>
      <Button asChild variant="gold" size="sm" className="shrink-0">
        <Link to="/me/verify">Get verified</Link>
      </Button>
    </div>
  );
}

function Field({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-1 text-destructive" aria-hidden="true">*</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted">{hint}</p>}
      {error && (
        <p role="alert" aria-live="polite" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
